import Head from "next/head";
import React from "react";
import { IGovernment, ICity } from "~/types/types";
import SeoHead from "~/components/seo/SeoHead";
import { useIntl } from "react-intl";
import { Tab } from "~/components/seo/common";

interface IGovernmentPageSeoProps {
  government: Pick<IGovernment, "logo"> & {
    city: Pick<ICity, "name" | "governmentType" | "state" | "country"> | null;
  };
  tab: Tab | string;
}

export default function GovernmentPageSeo({ government, tab }: IGovernmentPageSeoProps) {
  if (!government.city) {
    return null;
  }

  let formatTab = tab;
  if (tab === "connect") {
    formatTab = "profile";
  }

  return (
    <>
      <SeoHead
        title={getGovernmentFormattedTitle(formatTab, government)}
        description={getGovernmentFormattedDescription(formatTab, government)}
      />
      <Head>
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@Govlaunch" />
        <meta name="twitter:image" content={government.logo || ""} />

        <meta property="og:image" content={government.logo || ""} />
      </Head>
    </>
  );
}

export function getGovernmentFormattedTitle(
  tab: Tab | string,
  government: Pick<IGovernment, "logo"> & {
    city: Pick<ICity, "name" | "governmentType" | "state" | "country"> | null;
  },
) {
  const intl = useIntl();

  if (!government || !government.city) {
    return intl.formatMessage({
      defaultMessage: 'Search the wiki for local government innovation.', id: 'Pa6P7h',
    });
  }

  const location = government.city.country === "US" ? government.city.state : government.city.country;

  switch (tab) {
    case "projects":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Projects', id: 'WpWK2E' },
        { government: `${government.city.name}, ${location}` },
      );
    case "products":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Products', id: 'wraQhx' },
        { government: `${government.city.name}, ${location}` },
      );
    case "stories":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Stories', id: '5EasiY' },
        { government: `${government.city.name}, ${location}` },
      );
    case "profile":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Profile', id: 'nmeTHz' },
        { government: `${government.city.name}, ${location}` },
      );
    case "actions":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Actions', id: 'YQypWv' },
        { government: `${government.city.name}, ${location}` },
      );
    case "settings":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Settings', id: 'F/o1NJ' },
        { government: `${government.city.name}, ${location}` },
      );
    case "sharing":
      return intl.formatMessage(
        { defaultMessage: '{government} – Govlaunch Sharing', id: 'k85rsY' },
        { government: `${government.city.name}, ${location}` },
      );
    default:
      return tab;
  }
}

export function getGovernmentFormattedDescription(
  tab: Tab | string,
  government: Pick<IGovernment, "logo"> & {
    city: Pick<ICity, "name" | "governmentType" | "state" | "country"> | null;
  },
) {
  const intl = useIntl();

  if (!government || !government.city) {
    return intl.formatMessage({
      defaultMessage: 'Search the wiki for local government innovation.', id: 'Pa6P7h',
    });
  }

  const location = government.city.country === "US" ? government.city.state : government.city.country;

  switch (tab) {
    case "projects":
      return intl.formatMessage(
        {
          defaultMessage: 'Local government innovation projects from {government}. Search the wiki for local government innovation.', id: '5NIUrk',
        },
        { government: `${government.city.name}, ${location}` },
      );
    case "products":
      return intl.formatMessage(
        {
          defaultMessage: 'Local government technology products used by {government}. Search government technology products on Govlaunch.', id: 'VuTieF',
        },
        { government: `${government.city.name}, ${location}` },
      );
    case "stories":
      return intl.formatMessage(
        {
          defaultMessage: 'Local government innovation stories from {government}. Search the wiki for local government innovation.', id: '8PCliH',
        },
        { government: `${government.city.name}, ${location}` },
      );
    case "profile":
      return intl.formatMessage(
        { defaultMessage: '{government} local government profile. Search the wiki for local government innovation.', id: 'I0EQDk' },
        { government: `${government.city.name}, ${location}` },
      );
    default:
      return `${government.city.name}, ${location} ${tab}`;
  }
}
